import React, { createContext, useContext, useState, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';

const ThemeContext = createContext();

const ACCENT_STORAGE_KEY = 'app_accent_color';

// Same 15-color palette as the mobile app (react-app-gym's PRIMARY_PALETTES)
// so picking "blue" (etc.) looks like the same brand color on both platforms.
// Each entry is [pale, main, dark, darkest] as "r g b" strings, taken from
// that palette's 100/600/800/900 steps.
const ACCENT_PALETTES = {
  green:   ['224 240 232', '45 106 79',  '20 53 39',  '14 38 28'],
  purple:  ['245 243 255', '139 92 246', '91 33 182', '76 29 149'],
  blue:    ['239 246 255', '59 130 246', '30 64 175', '30 58 138'],
  red:     ['254 242 242', '239 68 68',  '153 27 27', '127 29 29'],
  yellow:  ['255 251 235', '245 158 11', '146 64 14', '120 53 15'],
  teal:    ['240 253 250', '20 184 166', '17 94 89',  '19 78 74'],
  orange:  ['255 247 237', '249 115 22', '194 65 12', '154 52 18'],
  pink:    ['253 242 248', '236 72 153', '190 24 93', '157 23 77'],
  indigo:  ['238 242 255', '99 102 241', '67 56 202', '55 48 163'],
  cyan:    ['236 254 255', '6 182 212',  '14 116 144','21 94 117'],
  lime:    ['247 254 231', '132 204 22', '77 124 15', '63 98 18'],
  rose:    ['255 241 242', '244 63 94',  '190 18 60', '159 18 57'],
  brown:   ['239 235 233', '121 85 72',  '93 64 55',  '78 52 46'],
  slate:   ['248 250 252', '100 116 139','51 65 85',  '30 41 59'],
  magenta: ['253 244 255', '217 70 239', '162 28 175','134 25 143'],
};

export const ACCENT_COLORS = Object.keys(ACCENT_PALETTES);

const rgbToHex = (rgbString) =>
  '#' + rgbString.split(' ').map((n) => Number(n).toString(16).padStart(2, '0')).join('');

export const ACCENT_SOLID_HEX = Object.fromEntries(
  Object.entries(ACCENT_PALETTES).map(([name, [, main]]) => [name, rgbToHex(main)])
);

const getStoredAccent = () => {
  const saved = typeof window !== 'undefined' ? localStorage.getItem(ACCENT_STORAGE_KEY) : null;
  return saved && ACCENT_COLORS.includes(saved) ? saved : 'green';
};

// ── Brand tokens ───────────────────────────────────────────────────────────
// leaf/pine/leafSoft/evergreen follow the selected accent color; everything
// else (gold, ink, paper...) stays fixed regardless of accent.
const buildColors = (accentColor) => {
  const [paleRgb, mainRgb, darkRgb, darkestRgb] = ACCENT_PALETTES[accentColor] || ACCENT_PALETTES.green;
  return {
    evergreen:  rgbToHex(darkestRgb), // sidebar / darkest surface
    pine:       rgbToHex(darkRgb),    // hover states on dark bg
    leaf:       rgbToHex(mainRgb),    // primary action, active, success
    leafSoft:   rgbToHex(paleRgb),    // light accent tint backgrounds
    gold:       '#C9A227',   // brand accent – use sparingly
    goldSoft:   '#F6EFD8',
    paper:      '#F6F5F0',   // page background
    card:       '#FFFFFF',   // card / panel background
    ink:        '#1B2420',   // primary text
    inkSoft:    '#5B6660',   // secondary text
    line:       '#E6E4DC',   // borders / dividers
    danger:     '#C0392B',
  };
};

export function ThemeProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [accentColor, setAccentColorState] = useState(getStoredAccent);

  const setAccentColor = (color) => {
    if (!ACCENT_COLORS.includes(color)) return;
    setAccentColorState(color);
    localStorage.setItem(ACCENT_STORAGE_KEY, color);
  };

  const COLORS = useMemo(() => buildColors(accentColor), [accentColor]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: {
            main:          COLORS.leaf,
            light:         '#2D9268',
            dark:          COLORS.pine,
            contrastText:  '#fff',
          },
          secondary: {
            main:         COLORS.gold,
            contrastText: COLORS.evergreen,
          },
          background: {
            default: isDarkMode ? '#121212' : COLORS.paper,
            paper:   isDarkMode ? '#1a1a1a' : COLORS.card,
          },
          text: {
            primary:   isDarkMode ? '#e2e8e4' : COLORS.ink,
            secondary: isDarkMode ? '#94a3b8'  : COLORS.inkSoft,
          },
          divider: COLORS.line,
          error:   { main: COLORS.danger },
        },
        shape: { borderRadius: 10 },
        typography: {
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: { fontFamily: '"Barlow Semi Condensed", sans-serif', fontWeight: 700 },
          h2: { fontFamily: '"Barlow Semi Condensed", sans-serif', fontWeight: 700 },
          h3: { fontFamily: '"Barlow Semi Condensed", sans-serif', fontWeight: 700 },
          h4: { fontFamily: '"Barlow Semi Condensed", sans-serif', fontWeight: 700 },
          h5: { fontFamily: '"Barlow Semi Condensed", sans-serif', fontWeight: 700 },
          h6: { fontFamily: '"Barlow Semi Condensed", sans-serif', fontWeight: 700 },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              containedPrimary: {
                background: COLORS.leaf,
                boxShadow: '0 4px 14px rgba(31,122,84,0.28)',
                '&:hover': {
                  background: COLORS.pine,
                  boxShadow: '0 6px 20px rgba(31,122,84,0.36)',
                },
              },
              outlinedPrimary: {
                borderColor: COLORS.leaf,
                color: COLORS.leaf,
                '&:hover': { borderColor: COLORS.pine, backgroundColor: COLORS.leafSoft },
              },
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundColor: isDarkMode ? '#1a1a1a' : COLORS.evergreen,
                color: '#DCE7E0',
                borderRight: 'none',
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                borderRadius: 9,
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.2)' : COLORS.leaf,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.3)' : COLORS.pine,
                  },
                },
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': { borderColor: COLORS.leaf },
                },
                '& label.Mui-focused': { color: COLORS.leaf },
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: { color: COLORS.leaf, '&.Mui-checked': { color: COLORS.leaf } },
            },
          },
          MuiStepIcon: {
            styleOverrides: {
              root: { '&.Mui-active': { color: COLORS.leaf }, '&.Mui-completed': { color: COLORS.gold } },
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: COLORS.leaf,
                  color: '#fff',
                  '&:hover': { backgroundColor: COLORS.pine },
                },
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: { backgroundColor: COLORS.evergreen, backgroundImage: 'none' },
            },
          },
          MuiChip: {
            styleOverrides: {
              colorPrimary: { backgroundColor: COLORS.leaf, color: '#fff' },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                '& .MuiTableCell-root': {
                  backgroundColor: COLORS.evergreen,
                  color: '#fff',
                },
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    [isDarkMode, COLORS]
  );

  const toggleTheme = () => setIsDarkMode((prev) => !prev);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, accentColor, setAccentColor, colors: COLORS }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
}
